import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../lib/useAuthFetch';
import { Card, SectionLabel, Pill, StatTile, DataTable, DecisionSummary, type Column } from '../components/ui';

const API = import.meta.env.VITE_API_URL ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Product {
  name: string;
  category: string;
  competitor_count: number;
  avg_price: number;
  min_price: number;
  max_price: number;
}

interface Location {
  id: string;
  name: string;
}

interface Competitor {
  competitor_id: string;
  competitor_name: string;
  slot_type: 'track' | 'block';
}

interface SeriesPoint {
  date: string; // 'YYYY-MM-DD'
  price: number;
  on_sale: boolean;
  discount_label: string | null;
}

interface HistoryData {
  product_name: string;
  days: number;
  competitors: Competitor[];
  series: Record<string, SeriesPoint[]>; // competitor_id → points
  own_price: number | null;
  own_on_sale: boolean | null;
  own_last_updated: string | null;
}

const fmtMoney = (n: number) => `$${n.toFixed(2)}`;
function fmtDate(iso: string | null): string {
  if (!iso) return 'unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unavailable';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Palette: assign a stable CannaSpy-token accent to each competitor index ────
// Distinct accents drawn from the Aurora+ palette — never Tailwind default
// green/amber/red. Block status is conveyed by the badge, not the line color.
const RIVAL_COLORS = ['var(--accent)', 'var(--warm)', 'var(--slate)', 'var(--rose)', '#7c6cff', '#3b8bd4'];
function rivalColor(idx: number) { return RIVAL_COLORS[idx % RIVAL_COLORS.length]; }

// ── SVG Chart helpers ─────────────────────────────────────────────────────────
const CHART_W = 900, CHART_H = 200;
const PAD = { top: 16, right: 20, bottom: 32, left: 48 };

function dateToDay(dateStr: string, startDate: Date): number {
  const d = new Date(dateStr);
  return Math.round((d.getTime() - startDate.getTime()) / 86_400_000);
}

function dayToX(day: number, totalDays: number) {
  return PAD.left + (day / Math.max(totalDays, 1)) * (CHART_W - PAD.left - PAD.right);
}

function priceToY(price: number, minP: number, maxP: number) {
  const range = maxP - minP || 1;
  const ratio = (price - minP) / range;
  return PAD.top + (1 - ratio) * (CHART_H - PAD.top - PAD.bottom);
}

function buildPath(pts: Array<{ day: number; price: number }>, minP: number, maxP: number, totalDays: number) {
  return pts.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${dayToX(p.day, totalDays).toFixed(1)} ${priceToY(p.price, minP, maxP).toFixed(1)}`
  ).join(' ');
}

// ── Empty / Loading states ────────────────────────────────────────────────────
function EmptyState({ msg }: { msg: string }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 12 }}>
      {msg}
    </div>
  );
}

// A single price observation, flattened across competitors for the log table.
interface ObsRow {
  key: string;
  date: string;
  competitor_name: string;
  slot_type: 'track' | 'block';
  price: number;
  on_sale: boolean;
  discount_label: string | null;
  color: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PriceHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authFetch = useAuthFetch();
  const requestedLocationId = searchParams.get('location_id');

  const [days, setDays] = useState(90);
  const [locations, setLocations] = useState<Location[]>([]);
  const [currentLocationId, setCurrentLocationId] = useState<string>('');
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryData | null>(null);
  const [hiddenLines, setHiddenLines] = useState<Record<string, boolean>>({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }

  useEffect(() => {
    setLoadingLocations(true);
    authFetch(`${API}/api/v1/locations`)
      .then(r => r.json())
      .then((data: { locations?: Location[] }) => {
        const locs = data.locations ?? [];
        setLocations(locs);
        const requested = requestedLocationId && locs.some(l => l.id === requestedLocationId)
          ? requestedLocationId
          : null;
        setCurrentLocationId(cur => requested || cur || locs[0]?.id || '');
      })
      .finally(() => setLoadingLocations(false));
  }, [authFetch, requestedLocationId]);

  // Fetch product list for location
  useEffect(() => {
    if (!currentLocationId) return;
    setLoadingProducts(true);
    authFetch(`${API}/api/v1/prices/products?location_id=${currentLocationId}`)
      .then(r => r.json())
      .then(data => {
        const prods: Product[] = data.data?.products || [];
        setProducts(prods);
        setSelectedProduct(prods[0]?.name ?? null);
        setLoadingProducts(false);
      })
      .catch(() => setLoadingProducts(false));
  }, [currentLocationId]);

  // Fetch history when product or days changes
  const fetchHistory = useCallback(() => {
    if (!currentLocationId || !selectedProduct) return;
    setLoadingHistory(true);
    const params = new URLSearchParams({
      location_id: currentLocationId,
      product_name: selectedProduct,
      days: String(days),
    });
    authFetch(`${API}/api/v1/prices/history-by-product?${params}`)
      .then(r => r.json())
      .then(data => {
        setHistoryData(data.data || null);
        setHiddenLines({});
        setLoadingHistory(false);
      })
      .catch(() => setLoadingHistory(false));
  }, [currentLocationId, selectedProduct, days]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  function toggleLine(id: string) {
    setHiddenLines(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // ── Derive chart data ───────────────────────────────────────────────────────
  const competitors = historyData?.competitors || [];
  const series = historyData?.series || {};

  // Build normalized series: [{ day, price }] relative to window start
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  startDate.setHours(0, 0, 0, 0);

  type NormPoint = { day: number; price: number; on_sale: boolean };
  const normSeries: Record<string, NormPoint[]> = {};
  for (const c of competitors) {
    const pts = series[c.competitor_id] || [];
    if (pts.length === 0) continue;
    normSeries[c.competitor_id] = pts.map(p => ({
      day: dateToDay(p.date, startDate),
      price: p.price,
      on_sale: p.on_sale,
    }));
  }

  // If only 1 data point, extend as a flat line to today
  for (const [cid, pts] of Object.entries(normSeries)) {
    if (pts.length === 1) {
      normSeries[cid] = [{ ...pts[0], day: 0 }, { ...pts[0], day: days }];
    }
  }

  const allPrices: number[] = [];
  for (const pts of Object.values(normSeries)) {
    pts.forEach(p => allPrices.push(p.price));
  }
  const minP = allPrices.length ? Math.min(...allPrices) - 2 : 0;
  const maxP = allPrices.length ? Math.max(...allPrices) + 2 : 100;

  // Grid lines
  const gridLines: React.ReactNode[] = [];
  for (let i = 0; i <= 4; i++) {
    const price = minP + (maxP - minP) * (i / 4);
    const y = priceToY(price, minP, maxP);
    gridLines.push(
      <line key={`g${i}`} x1={PAD.left} y1={y} x2={CHART_W - PAD.right} y2={y} stroke="currentColor" strokeOpacity="0.06" strokeWidth="1" />,
      <text key={`t${i}`} x={PAD.left - 5} y={y + 4} textAnchor="end" fontFamily="JetBrains Mono,monospace" fontSize="9" fill="currentColor" fillOpacity="0.4">${Math.round(price)}</text>
    );
  }

  // X-axis labels: evenly spaced dates over the window
  const xLabels: Array<{ label: string; day: number }> = [];
  const labelCount = days <= 30 ? 4 : days <= 60 ? 5 : 6;
  for (let i = 0; i <= labelCount; i++) {
    const day = Math.round((days / labelCount) * i);
    const d = new Date(startDate);
    d.setDate(d.getDate() + day);
    xLabels.push({ label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), day });
  }

  // Stats
  const allCurrentPrices = competitors
    .map(c => (series[c.competitor_id]?.[series[c.competitor_id].length - 1]?.price) ?? null)
    .filter(Boolean) as number[];
  const rivalLow = allCurrentPrices.length ? Math.min(...allCurrentPrices) : null;
  const rivalHigh = allCurrentPrices.length ? Math.max(...allCurrentPrices) : null;
  const rivalLowName = rivalLow != null
    ? competitors.find(c => series[c.competitor_id]?.[series[c.competitor_id].length - 1]?.price === rivalLow)?.competitor_name
    : null;

  const noLocation = !loadingLocations && !currentLocationId;
  const noProducts = !loadingProducts && products.length === 0;
  const noHistory = !loadingHistory && historyData && Object.keys(series).length === 0;
  const selectedProductMeta = products.find(p => p.name === selectedProduct) ?? null;
  const currentAvg = allCurrentPrices.length
    ? allCurrentPrices.reduce((a, b) => a + b, 0) / allCurrentPrices.length
    : null;
  const openingPrices = competitors
    .map(c => series[c.competitor_id]?.[0]?.price ?? null)
    .filter((price): price is number => price != null);
  const openingAvg = openingPrices.length
    ? openingPrices.reduce((a, b) => a + b, 0) / openingPrices.length
    : null;
  const averageMove = currentAvg != null && openingAvg != null ? currentAvg - openingAvg : null;
  const ownPrice = historyData?.own_price ?? null;
  const ownGapToAverage = ownPrice != null && currentAvg != null ? ownPrice - currentAvg : null;
  const ownGapToLow = ownPrice != null && rivalLow != null ? ownPrice - rivalLow : null;
  const ownObservedAt = historyData?.own_last_updated ?? null;
  const ownAgeDays = ownObservedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(ownObservedAt).getTime()) / 86_400_000))
    : null;
  const selectedLocationName = locations.find(l => l.id === currentLocationId)?.name ?? 'this location';

  // Flatten observations into table rows, preserving competitor + series order.
  const obsRows: ObsRow[] = competitors.flatMap((c, idx) =>
    (series[c.competitor_id] || []).map((pt, i) => ({
      key: `${c.competitor_id}-${i}`,
      date: pt.date,
      competitor_name: c.competitor_name,
      slot_type: c.slot_type,
      price: pt.price,
      on_sale: pt.on_sale,
      discount_label: pt.discount_label,
      color: rivalColor(idx),
    }))
  );

  const obsColumns: Column<ObsRow>[] = [
    { key: 'date', header: 'Date', mono: true, width: 104 },
    {
      key: 'rival',
      header: 'Rival',
      render: r => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: r.color, fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
          {r.competitor_name}
        </span>
      ),
    },
    { key: 'price', header: 'Price', align: 'right', width: 90, render: r => `$${r.price}` },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      width: 130,
      render: r => r.on_sale
        ? <Pill variant="chip" style={{ background: 'var(--warm-soft)', color: 'var(--warm)', textTransform: 'uppercase' }}>{r.discount_label || 'sale'}</Pill>
        : <span style={{ color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>—</span>,
    },
  ];

  const filterSelectStyle: React.CSSProperties = {
    padding: '5px 11px', borderRadius: 'var(--r-xs)', fontSize: 11, fontWeight: 600,
    border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', maxWidth: 280,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Topbar */}
      <div style={{ background: 'var(--surface)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', padding: '0 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0 10px' }}>
          <button onClick={() => navigate('/prices')} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', transition: 'var(--transition)', flexShrink: 0, padding: 0 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Price Intelligence
          </button>
          <div style={{ width: 1, height: 16, background: 'var(--border-2)' }} />
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 }}>
              Price History
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 1, marginTop: 3 }}>
              {days}-DAY TREND · LIVE DATA
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn" onClick={() => showToast('Exporting chart data as CSV…')} style={{ fontSize: 11, padding: '7px 13px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export CSV
            </button>
          </div>
        </div>
        {/* Sub-nav */}
        <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid var(--border)' }}>
          {[
            { label: 'Price History', to: '/prices/history', active: true },
            { label: 'Catalog Comparison', to: '/prices/catalog', active: false },
            { label: 'Brand Carriers', to: '/prices/brands', active: false },
          ].map(tab => (
            <div key={tab.label} onClick={() => navigate(tab.to)} style={{ padding: '10px 18px', fontSize: 12, fontWeight: 600, color: tab.active ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer', borderBottom: tab.active ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap', transition: 'var(--transition)' }}>
              {tab.label}
            </div>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <select
          aria-label="Location"
          value={currentLocationId}
          onChange={e => { setCurrentLocationId(e.target.value); setSelectedProduct(null); setHistoryData(null); }}
          disabled={loadingLocations || locations.length === 0}
          style={filterSelectStyle}>
          {loadingLocations && <option>Loading locations…</option>}
          {!loadingLocations && locations.length === 0 && <option>No locations found</option>}
          {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
        </select>
        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px' }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Product</span>
        <select
          value={selectedProduct ?? ''}
          onChange={e => { setSelectedProduct(e.target.value); setHiddenLines({}); }}
          disabled={loadingProducts || products.length === 0}
          style={filterSelectStyle}>
          {loadingProducts && <option>Loading products…</option>}
          {!loadingProducts && products.length === 0 && <option>No shared products found</option>}
          {products.map(p => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px' }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Window</span>
        <select
          value={String(days)}
          onChange={e => setDays(Number(e.target.value))}
          style={{ ...filterSelectStyle, maxWidth: undefined }}>
          <option value="30">30 Days</option>
          <option value="60">60 Days</option>
          <option value="90">90 Days</option>
        </select>
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
          {loadingHistory
            ? <><div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warm)' }} /> Fetching…</>
            : <><div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} /> Live data</>
          }
        </div>
      </div>

      {/* Content */}
      {noLocation ? (
        <EmptyState msg="Select a location to view price history." />
      ) : noProducts ? (
        <EmptyState msg="No products found across ≥2 competitors. Run the data pipeline to populate." />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {historyData && selectedProduct && (
            <DecisionSummary
              className="price-history-summary"
              question="How has this product's market price moved, and does it need attention?"
              priority={ownAgeDays != null && ownAgeDays > 30 ? 'watch' : ownGapToAverage != null && ownGapToAverage > 0.5 ? 'high' : 'healthy'}
              answer={ownPrice != null && ownGapToAverage != null
                ? `${selectedProduct} was last observed at ${fmtMoney(ownPrice)}, ${fmtMoney(Math.abs(ownGapToAverage))} ${ownGapToAverage >= 0 ? 'above' : 'below'} the current rival average`
                : currentAvg != null
                  ? `${selectedProduct} currently averages ${fmtMoney(currentAvg)} across ${allCurrentPrices.length} rivals with observations`
                  : `Price movement is not yet available for ${selectedProduct}`}
              change={averageMove != null && openingAvg != null && currentAvg != null
                ? `The rival average moved from ${fmtMoney(openingAvg)} to ${fmtMoney(currentAvg)} (${averageMove >= 0 ? '+' : '−'}${fmtMoney(Math.abs(averageMove))}) within the ${days}-day window. The current observed range is ${rivalLow != null ? fmtMoney(rivalLow) : 'unavailable'}–${rivalHigh != null ? fmtMoney(rivalHigh) : 'unavailable'}.`
                : `The current observed range is ${rivalLow != null ? fmtMoney(rivalLow) : 'unavailable'}–${rivalHigh != null ? fmtMoney(rivalHigh) : 'unavailable'}; a comparable opening average is unavailable.`}
              impact={ownPrice != null
                ? `The last observed ${selectedLocationName} price is ${ownGapToLow != null ? `${fmtMoney(Math.abs(ownGapToLow))} ${ownGapToLow >= 0 ? 'above' : 'below'} the market low` : 'not comparable with the market low'}, but that own-menu observation is ${ownAgeDays != null ? `${ownAgeDays} days old` : 'not dated'}. Current positioning should be verified before a pricing decision.`
                : `${selectedLocationName} does not have a matched own-menu price for this product, so CannaSpy cannot determine market position or commercial exposure.`}
              comparison={{ baseline: 'the first available rival observation', period: `${days}-day window` }}
              provenance={{ updatedAt: fmtDate(ownObservedAt), source: 'own menu and rival price observations', confidence: ownAgeDays != null && ownAgeDays > 30 ? 'low' : 'medium' }}
              action={selectedProductMeta ? (
                <button
                  className="btn btn-primary"
                  data-testid="price-history-review-category"
                  onClick={() => navigate(`/prices?view=priority&category=${encodeURIComponent(selectedProductMeta.category)}&location_id=${encodeURIComponent(currentLocationId)}`)}
                >
                  Review {selectedProductMeta.category} prices →
                </button>
              ) : undefined}
            />
          )}

          {/* Chart card */}
          <Card style={{ padding: '20px 22px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
                  {selectedProduct ?? '—'} — Price History
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  {competitors.length} rival{competitors.length !== 1 ? 's' : ''} tracked · last {days} days
                </div>
              </div>
              <button className="btn" onClick={() => showToast('Setting price alert for this product…')} style={{ fontSize: 11, padding: '7px 13px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                Set alert
              </button>
            </div>

            {/* Stat strip */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatTile
                size="sm"
                label="Rival Low"
                tone="accent"
                value={rivalLow != null ? `$${rivalLow}` : '—'}
                delta={rivalLowName ?? 'No data yet'}
                style={{ flex: 1, minWidth: 120 }}
              />
              <StatTile
                size="sm"
                label="Rival High"
                value={rivalHigh != null ? `$${rivalHigh}` : '—'}
                delta={`${competitors.length} rivals tracked`}
                style={{ flex: 1, minWidth: 120 }}
              />
              <StatTile
                size="sm"
                label="Avg Price"
                value={allCurrentPrices.length
                  ? `$${(allCurrentPrices.reduce((a, b) => a + b, 0) / allCurrentPrices.length).toFixed(2)}`
                  : '—'}
                delta="across rivals"
                style={{ flex: 1, minWidth: 120 }}
              />
              <StatTile
                size="sm"
                label="Data Points"
                value={String(Object.values(series).reduce((s, pts) => s + pts.length, 0))}
                delta={`${days}-day window`}
                style={{ flex: 1, minWidth: 120 }}
              />
            </div>

            {/* SVG Chart */}
            {noHistory ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11, border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
                No price data in this window yet — the next pipeline run will populate this chart.
              </div>
            ) : loadingHistory ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>
                Pulling latest prices…
              </div>
            ) : (
              <>
                <div style={{ position: 'relative', width: '100%', height: 220 }}>
                  <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
                    {gridLines}
                    {xLabels.map(m => (
                      <text key={m.label} x={dayToX(m.day, days)} y={CHART_H - PAD.bottom + 14} textAnchor="middle" fontFamily="JetBrains Mono,monospace" fontSize="9" fill="currentColor" fillOpacity="0.4">{m.label}</text>
                    ))}
                    {competitors.map((c, idx) => {
                      const pts = normSeries[c.competitor_id];
                      if (!pts || hiddenLines[c.competitor_id]) return null;
                      const color = rivalColor(idx);
                      const d = buildPath(pts, minP, maxP, days);
                      return (
                        <g key={c.competitor_id}>
                          <path d={d} fill="none" stroke={color} strokeWidth="2" />
                          {pts.filter((_, i) => i > 0 && i < pts.length - 1).map((p, i) => (
                            <circle key={i} cx={dayToX(p.day, days)} cy={priceToY(p.price, minP, maxP)} r="3.5" fill={color} stroke="var(--surface)" strokeWidth="1.5" />
                          ))}
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
                  {competitors.map((c, idx) => {
                    const pts = normSeries[c.competitor_id];
                    if (!pts) return null;
                    const color = rivalColor(idx);
                    const lastPrice = series[c.competitor_id]?.slice(-1)[0]?.price;
                    return (
                      <div key={c.competitor_id} onClick={() => toggleLine(c.competitor_id)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', opacity: hiddenLines[c.competitor_id] ? 0.3 : 1, transition: 'opacity 0.15s' }}>
                        <div style={{ width: 18, height: 2.5, borderRadius: 2, background: color, flexShrink: 0 }} />
                        <span>{c.competitor_name}</span>
                        {lastPrice != null && <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>${lastPrice}</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* Price observations log */}
          {!noHistory && !loadingHistory && (
            <div style={{ marginTop: 20 }}>
              <SectionLabel style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                Price Observations
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </SectionLabel>
              <Card variant="sm" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                  columns={obsColumns}
                  rows={obsRows}
                  rowKey={r => r.key}
                  empty="No price observations in this window yet — the next pipeline run will populate them."
                />
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '11px 18px', fontSize: 12, fontWeight: 600, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--sans)' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          {toast}
        </div>
      )}
    </div>
  );
}
